import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n/LangContext';
import LangToggle from '../components/LangToggle';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`,
  };
}

const STATUS_COLORS = {
  pending: '#f59e0b',
  draft: '#5b6af0',
  published: '#10b981',
};

function AnalyticsPanel({ analytics, t, s }) {
  const totals = analytics?.totals || {};
  const pageStats = Array.isArray(analytics?.pageStats) ? analytics.pageStats : [];
  const dailyStats = Array.isArray(analytics?.dailyStats) ? analytics.dailyStats : [];
  const userStats = Array.isArray(analytics?.userStats) ? analytics.userStats : [];
  const totalViews = Number(totals?.total_views || 0);
  const hasData = totalViews > 0;

  const today = new Date();
  const chartDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    const key = d.toISOString().slice(0, 10);
    const found = dailyStats.find((r) => r.date === key);
    return {
      label: d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' }),
      views: Number(found?.views || 0),
    };
  });
  const maxViews = Math.max(...chartDays.map((d) => d.views), 1);

  return (
    <>
      <div style={s.statsRow}>
        {[
          { label: t('adminAnalyticsTotalViews'),     value: totals?.total_views     || 0, color: '#5b6af0' },
          { label: t('adminAnalyticsUniqueVisitors'), value: totals?.unique_visitors || 0, color: '#10b981' },
          { label: t('adminAnalyticsToday'),          value: totals?.views_today     || 0, color: '#f59e0b' },
          { label: t('adminAnalytics7d'),             value: totals?.views_7d        || 0, color: '#1a1a2e' },
        ].map((st) => (
          <div key={st.label} style={s.statCard}>
            <span style={{ ...s.statValue, color: st.color }}>{st.value}</span>
            <span style={s.statLabel}>{st.label}</span>
          </div>
        ))}
      </div>

      {!hasData && (
        <div style={{ ...s.tableWrap, padding: '48px', textAlign: 'center', color: '#aaa', fontSize: '14px' }}>
          {t('adminAnalyticsNoData')}
        </div>
      )}

      {hasData && (
        <>
          <div style={{ ...s.tableWrap, padding: '24px', marginBottom: '20px' }}>
            <p style={{ fontSize: '13px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px' }}>
              {t('adminAnalyticsLast7Days')}
            </p>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', height: '140px' }}>
              {chartDays.map((day) => (
                <div key={day.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%', justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: day.views > 0 ? '#5b6af0' : '#ccc' }}>{day.views || ''}</span>
                  <div style={{ width: '100%', background: day.views > 0 ? '#5b6af0' : '#f0f1f5', borderRadius: '4px 4px 0 0', height: `${Math.max((day.views / maxViews) * 100, day.views > 0 ? 4 : 2)}%`, transition: 'height .4s ease' }} />
                  <span style={{ fontSize: '10px', color: '#aaa', textAlign: 'center', lineHeight: 1.2 }}>{day.label}</span>
                </div>
              ))}
            </div>
          </div>

          <p style={{ fontSize: '13px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>
            {t('adminAnalyticsPageBreakdown')}
          </p>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {[t('adminAnalyticsColPage'), t('adminAnalyticsColUser'), t('adminAnalyticsColTotal'), t('adminAnalyticsColUnique'), t('adminAnalyticsColToday'), t('adminAnalyticsCol7d')].map((h) => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageStats.map((p, i) => (
                  <tr key={p.id} style={i % 2 !== 0 ? { background: '#f9f9fb' } : {}}>
                    <td style={{ ...s.td, maxWidth: '260px' }}>
                      <span title={p.description}>{p.description?.length > 60 ? p.description.slice(0, 60) + '…' : p.description}</span>
                    </td>
                    <td style={s.td}>{p.user_email || '—'}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: '#5b6af0' }}>{p.total_views}</td>
                    <td style={{ ...s.td, color: '#10b981', fontWeight: 600 }}>{p.unique_visitors}</td>
                    <td style={s.td}>{p.views_today}</td>
                    <td style={s.td}>{p.views_7d}</td>
                  </tr>
                ))}
                {pageStats.length === 0 && (
                  <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#aaa' }}>{t('adminAnalyticsNoData')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── REGISTERED USERS ── always shown */}
      <p style={{ fontSize: '13px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '1px', margin: '28px 0 12px' }}>
        Registered Users ({userStats.length})
      </p>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              {['Email', 'Joined', 'Total Pages', 'Published', 'Pending', 'Last Activity'].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {userStats.map((u, i) => (
              <tr key={u.id} style={i % 2 !== 0 ? { background: '#f9f9fb' } : {}}>
                <td style={{ ...s.td, fontWeight: 600 }}>{u.email}</td>
                <td style={s.td}>{new Date(u.joined_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                <td style={{ ...s.td, fontWeight: 700, color: '#5b6af0' }}>{u.total_pages}</td>
                <td style={{ ...s.td, color: '#10b981', fontWeight: 600 }}>{u.published_pages}</td>
                <td style={{ ...s.td, color: '#f59e0b' }}>{u.pending_pages}</td>
                <td style={s.td}>{u.last_page_at ? new Date(u.last_page_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
              </tr>
            ))}
            {userStats.length === 0 && (
              <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#aaa' }}>No registered users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { t } = useT();
  const [tab, setTab] = useState('requests'); // 'requests' | 'pages' | 'candidates' | 'analytics'
  const [requests, setRequests] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState('ok');
  const [search, setSearch] = useState('');
  const [generatingId, setGeneratingId] = useState('');

  // Analytics tab state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Candidates tab state
  const [candidates, setCandidates] = useState([]);
  const [candTotal, setCandTotal] = useState(0);
  const [candPage, setCandPage] = useState(1);
  const [candSearch, setCandSearch] = useState('');
  const [candSearchInput, setCandSearchInput] = useState('');
  const [candLoading, setCandLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candDrawerLoading, setCandDrawerLoading] = useState(false);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAll() {
    setLoading(true);
    setError('');
    try {
      const [reqRes, pagesRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/requests`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/pages`, { headers: authHeaders() }),
      ]);
      if (reqRes.status === 401 || reqRes.status === 403) {
        localStorage.removeItem('adminToken');
        navigate('/auth');
        return;
      }
      const reqData = await reqRes.json();
      const pagesData = await pagesRes.json();
      setRequests(reqData.requests || []);
      setPages(pagesData.pages || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function showToast(msg, type = 'ok') {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(''), 3000);
  }

  function logout() {
    localStorage.removeItem('adminToken');
    navigate('/auth');
  }

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/analytics`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || data?.message || 'Failed to load analytics');
      setAnalytics(data);
    } catch (err) {
      showToast(err.message, 'err');
      setAnalytics({ totals: {}, pageStats: [], dailyStats: [] });
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'analytics') loadAnalytics();
  }, [tab, loadAnalytics]);

  const loadCandidates = useCallback(async () => {
    setCandLoading(true);
    try {
      const params = new URLSearchParams({ page: candPage, pageSize: 15 });
      if (candSearch) params.set('search', candSearch);
      const res = await fetch(`${API_BASE}/api/crm/candidates?${params}`, { headers: authHeaders() });
      const data = await res.json();
      setCandidates(data.candidates || []);
      setCandTotal(data.pagination?.total || 0);
    } catch (err) {
      showToast(err.message, 'err');
    } finally {
      setCandLoading(false);
    }
  }, [candPage, candSearch]);

  useEffect(() => {
    if (tab === 'candidates') loadCandidates();
  }, [tab, loadCandidates]);

  async function openCandidateDrawer(id) {
    setCandDrawerLoading(true);
    setSelectedCandidate(null);
    try {
      const res = await fetch(`${API_BASE}/api/crm/candidates/${id}`, { headers: authHeaders() });
      const data = await res.json();
      setSelectedCandidate(data);
    } catch (err) {
      showToast(err.message, 'err');
    } finally {
      setCandDrawerLoading(false);
    }
  }

  async function updateCandStage(candidateId, stage) {
    try {
      await fetch(`${API_BASE}/api/crm/candidates/${candidateId}/stage`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ stage }),
      });
      setCandidates((prev) => prev.map((c) => c.id === candidateId ? { ...c, stage } : c));
      if (selectedCandidate?.candidate?.id === candidateId) {
        setSelectedCandidate((prev) => ({ ...prev, candidate: { ...prev.candidate, stage } }));
      }
    } catch (err) {
      showToast(err.message, 'err');
    }
  }

  async function generatePage(requestId) {
    setGeneratingId(requestId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/requests/${requestId}/generate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ provider: 'openai' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || 'Generation failed');
      sessionStorage.setItem('adminEditing', 'true');
      navigate(`/editor/${requestId}`);
    } catch (err) {
      showToast(`Error: ${err.message}`, 'err');
      setGeneratingId('');
    }
  }

  async function publishPage(pageId) {
    try {
      const res = await fetch(`${API_BASE}/api/admin/pages/${pageId}/publish`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Publish failed');
      showToast(t('adminToastPublished'));
      await loadAll();
    } catch (err) {
      showToast(`Error: ${err.message}`, 'err');
    }
  }

  async function unpublishPage(pageId) {
    try {
      const res = await fetch(`${API_BASE}/api/admin/pages/${pageId}/unpublish`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Unpublish failed');
      showToast(t('adminToastUnpublished'));
      await loadAll();
    } catch (err) {
      showToast(`Error: ${err.message}`, 'err');
    }
  }

  async function deletePage(id) {
    if (!window.confirm(t('adminConfirmDelete'))) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/pages/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Delete failed');
      setPages((prev) => prev.filter((p) => p.id !== id));
      setRequests((prev) => prev.filter((r) => r.id !== id));
      showToast(t('adminToastDeleted'));
    } catch (err) {
      showToast(`Error: ${err.message}`, 'err');
    }
  }

  async function downloadExport(format) {
    const res = await fetch(`${API_BASE}/api/admin/pages/export?format=${format}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
    });
    if (!res.ok) { showToast('Export failed', 'err'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = format === 'csv' ? 'pages.csv' : 'pages.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  const filteredPages = pages.filter((p) =>
    p.description.toLowerCase().includes(search.toLowerCase()) ||
    p.userEmail.toLowerCase().includes(search.toLowerCase())
  );

  const pendingCount = requests.length;
  const draftPages = pages.filter((p) => p.status === 'draft');
  const publishedPages = pages.filter((p) => p.status === 'published');

  return (
    <div style={s.wrap}>
      {toast && (
        <div style={{ ...s.toast, borderColor: toastType === 'err' ? '#e74c3c' : '#10b981', color: toastType === 'err' ? '#e74c3c' : '#fff' }}>
          {toast}
        </div>
      )}

      {/* NAV */}
      <div style={s.nav}>
        <span style={s.navLogo}>FORMLY HR — Admin</span>
        <div style={s.navR}>
          <span style={s.navCount}>{pages.length} {t('adminNavPages')} · {pendingCount} {t('adminNavPending')}</span>
          <LangToggle />
          <button style={s.logoutBtn} onClick={logout}>{t('adminLogout')}</button>
        </div>
      </div>

      <div style={s.shell}>
        {/* STATS ROW */}
        <div style={s.statsRow}>
          {[
            { label: t('adminPendingRequests'), value: pendingCount,           color: '#f59e0b' },
            { label: t('adminDraftPages'),       value: draftPages.length,      color: '#5b6af0' },
            { label: t('adminPublished'),        value: publishedPages.length,  color: '#10b981' },
            { label: t('adminTotalPages'),       value: pages.length,           color: '#aaa'    },
          ].map((stat) => (
            <div key={stat.label} style={s.statCard}>
              <span style={{ ...s.statValue, color: stat.color }}>{stat.value}</span>
              <span style={s.statLabel}>{stat.label}</span>
            </div>
          ))}
        </div>

        {/* TABS */}
        <div style={s.tabRow}>
          <button style={{ ...s.tab, ...(tab === 'requests' ? s.tabActive : {}) }} onClick={() => setTab('requests')}>
            {t('adminTabRequests')} {pendingCount > 0 && <span style={s.badge}>{pendingCount}</span>}
          </button>
          <button style={{ ...s.tab, ...(tab === 'pages' ? s.tabActive : {}) }} onClick={() => setTab('pages')}>
            {t('adminTabPages')}
          </button>
          <button style={{ ...s.tab, ...(tab === 'candidates' ? s.tabActive : {}) }} onClick={() => setTab('candidates')}>
            {t('adminTabCandidates')} {candTotal > 0 && <span style={s.badge}>{candTotal}</span>}
          </button>
          <button style={{ ...s.tab, ...(tab === 'analytics' ? s.tabActive : {}) }} onClick={() => setTab('analytics')}>
            {t('adminTabAnalytics')}
          </button>
          <button style={{ ...s.tab, marginLeft: 'auto' }} onClick={() => navigate('/meta')}>
            📢 Meta Campaigns
          </button>
        </div>

        {loading && <p style={s.msg}>{t('adminLoading')}</p>}
        {error && <p style={{ ...s.msg, color: '#e74c3c' }}>{error}</p>}

        {/* ── PAGE REQUESTS TAB ── */}
        {!loading && tab === 'requests' && (
          <>
            <div style={s.headerRow}>
              <h2 style={s.title}>{t('adminTabRequests')}</h2>
              <button style={s.btnSecondary} onClick={loadAll}>{t('adminRefresh')}</button>
            </div>
            {requests.length === 0 ? (
              <p style={{ ...s.msg, textAlign: 'left' }}>{t('adminNoRequests')}</p>
            ) : (
              <div style={s.cardList}>
                {requests.map((r) => (
                  <div key={r.id} style={s.requestCard}>
                    <div style={s.requestCardTop}>
                      <div style={{ flex: 1 }}>
                        <div style={s.requestUser}>{r.userEmail}</div>
                        <div style={s.requestDesc} title={r.description}>
                          {r.description.length > 120 ? r.description.slice(0, 120) + '…' : r.description}
                        </div>
                        <div style={s.requestMeta}>
                          {new Date(r.createdAt).toLocaleDateString('de-DE')} ·
                          <span style={{ ...s.statusPill, background: `${STATUS_COLORS[r.status]}22`, color: STATUS_COLORS[r.status], border: `1px solid ${STATUS_COLORS[r.status]}55` }}>
                            {r.status}
                          </span>
                        </div>
                      </div>
                      <div style={s.requestActions}>
                        <button
                          style={{ ...s.btnPrimary, opacity: generatingId === r.id ? 0.6 : 1 }}
                          disabled={!!generatingId}
                          onClick={() => generatePage(r.id)}
                        >
                          {generatingId === r.id ? t('adminGenerating') : t('adminGenerate')}
                        </button>
                        <button style={s.btnSecondary} onClick={() => deletePage(r.id)}>{t('adminDelete')}</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── ALL PAGES TAB ── */}
        {!loading && tab === 'pages' && (
          <>
            <div style={s.headerRow}>
              <h2 style={s.title}>{t('adminTabPages')}</h2>
              <div style={s.actions}>
                <button style={s.btnSecondary} onClick={loadAll}>{t('adminRefresh')}</button>
                <button style={s.btnSecondary} onClick={() => downloadExport('csv')}>{t('adminExportCsv')}</button>
                <button style={s.btnSecondary} onClick={() => downloadExport('json')}>{t('adminExportJson')}</button>
              </div>
            </div>

            <input
              style={s.search}
              placeholder={t('adminSearchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {[
                      t('adminColNum'), t('adminColUser'), t('adminColDescription'),
                      t('adminColStatus'), t('adminColCreated'), t('adminColLiveUrl'), t('adminColActions'),
                    ].map((h) => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPages.length === 0 && (
                    <tr><td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#aaa' }}>{t('adminNoPages')}</td></tr>
                  )}
                  {filteredPages.map((p, i) => (
                    <tr key={p.id} style={i % 2 === 0 ? {} : { background: '#f9f9fb' }}>
                      <td style={s.td}>{i + 1}</td>
                      <td style={s.td}>{p.userEmail}</td>
                      <td style={{ ...s.td, maxWidth: '260px' }}>
                        <span title={p.description}>
                          {p.description.length > 70 ? p.description.slice(0, 70) + '…' : p.description}
                        </span>
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.statusPill, background: `${STATUS_COLORS[p.status] || '#aaa'}22`, color: STATUS_COLORS[p.status] || '#aaa', border: `1px solid ${STATUS_COLORS[p.status] || '#aaa'}55` }}>
                          {p.status}
                        </span>
                      </td>
                      <td style={s.td}>{new Date(p.createdAt).toLocaleDateString('de-DE')}</td>
                      <td style={s.td}>
                        <a href={p.liveUrl} target="_blank" rel="noreferrer" style={s.link}>{t('adminOpenPage')}</a>
                      </td>
                      <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {p.status === 'draft' && (
                            <button style={s.actionBtn} onClick={() => { sessionStorage.setItem('adminEditing', 'true'); navigate(`/editor/${p.id}`); }}>{t('adminEdit')}</button>
                          )}
                          {p.status === 'draft' && (
                            <button style={{ ...s.actionBtn, background: '#d1fae5', color: '#065f46', borderColor: '#6ee7b7' }} onClick={() => publishPage(p.id)}>{t('adminPublish')}</button>
                          )}
                          {p.status === 'published' && (
                            <button style={s.actionBtn} onClick={() => { sessionStorage.setItem('adminEditing', 'true'); navigate(`/editor/${p.id}`); }}>{t('adminEdit')}</button>
                          )}
                          {p.status === 'published' && (
                            <button style={{ ...s.actionBtn, background: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' }} onClick={() => unpublishPage(p.id)}>{t('adminUnpublish')}</button>
                          )}
                          <button style={{ ...s.actionBtn, background: '#fff5f5', color: '#e74c3c', borderColor: '#fca5a5' }} onClick={() => deletePage(p.id)}>{t('adminDelete')}</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── CANDIDATES TAB ── */}
        {tab === 'candidates' && (
          <>
            <div style={s.headerRow}>
              <h2 style={s.title}>{t('adminTabCandidates')}</h2>
              <button style={s.btnSecondary} onClick={loadCandidates}>{t('adminRefresh')}</button>
            </div>

            <form style={{ display: 'flex', gap: '8px', marginBottom: '16px' }} onSubmit={(e) => { e.preventDefault(); setCandPage(1); setCandSearch(candSearchInput.trim()); }}>
              <input style={{ ...s.search, marginBottom: 0, flex: 1 }} placeholder={t('adminCandSearchPlaceholder')} value={candSearchInput} onChange={(e) => setCandSearchInput(e.target.value)} />
              <button style={s.btnPrimary} type="submit">{t('adminSearch')}</button>
              {candSearch && <button style={s.btnSecondary} type="button" onClick={() => { setCandSearchInput(''); setCandSearch(''); setCandPage(1); }}>{t('adminClearSearch')}</button>}
            </form>

            {candLoading && <p style={s.msg}>{t('adminLoading')}</p>}

            {!candLoading && candidates.length === 0 && (
              <p style={{ ...s.msg, textAlign: 'left' }}>{t('adminNoCandidates')}</p>
            )}

            {!candLoading && candidates.length > 0 && (
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      {[
                        t('adminColName'), t('adminColEmail'), t('adminColPhone'),
                        t('adminColJob'), t('adminColApplied'), t('adminColStage'),
                      ].map((h) => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c, i) => (
                      <tr key={c.id} style={{ cursor: 'pointer', ...(i % 2 !== 0 ? { background: '#f9f9fb' } : {}) }} onClick={() => openCandidateDrawer(c.id)}>
                        <td style={s.td}>{c.full_name || '—'}</td>
                        <td style={s.td}>{c.email || '—'}</td>
                        <td style={s.td}>{c.phone || '—'}</td>
                        <td style={s.td}>{c.job_title || '—'}</td>
                        <td style={s.td}>{new Date(c.created_at).toLocaleDateString('de-DE')}</td>
                        <td style={s.td} onClick={(e) => e.stopPropagation()}>
                          <select value={c.stage} onChange={(e) => updateCandStage(c.id, e.target.value)} style={{ fontSize: '12px', padding: '3px 6px', borderRadius: '4px', border: '1px solid #ddd' }}>
                            {['applied','screening','interview','offer','hired','rejected'].map((st) => (
                              <option key={st} value={st}>{st}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {candTotal > 15 && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
                <button style={s.btnSecondary} disabled={candPage <= 1} onClick={() => setCandPage((p) => p - 1)}>{t('adminPrev')}</button>
                <span style={{ fontSize: '13px', color: '#888' }}>{t('adminPageOf', { page: candPage, total: candTotal })}</span>
                <button style={s.btnSecondary} disabled={candPage * 15 >= candTotal} onClick={() => setCandPage((p) => p + 1)}>{t('adminNext')}</button>
              </div>
            )}

            {/* Candidate detail drawer */}
            {(candDrawerLoading || selectedCandidate) && (
              <div style={{ marginTop: '20px', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{t('adminCandidateDetails')}</h3>
                  <button style={s.btnSecondary} onClick={() => setSelectedCandidate(null)}>{t('adminClose')}</button>
                </div>
                {candDrawerLoading && <p style={s.msg}>{t('adminLoading')}</p>}
                {!candDrawerLoading && selectedCandidate?.candidate && (() => {
                  const cd = selectedCandidate.candidate;
                  const tl = selectedCandidate.timeline || [];
                  return (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: '14px', marginBottom: '16px' }}>
                        {[
                          [t('adminColName'), cd.full_name],
                          [t('adminColEmail'), cd.email],
                          [t('adminColPhone'), cd.phone],
                          [t('adminColJob'), cd.job_title],
                          [t('adminColStage'), cd.stage],
                          [t('adminColApplied'), new Date(cd.created_at).toLocaleDateString('de-DE')],
                        ].map(([k, v]) => (
                          <div key={k}><span style={{ color: '#888', fontSize: '12px' }}>{k}</span><br /><strong>{v || '—'}</strong></div>
                        ))}
                      </div>
                      {cd.notes && <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', fontSize: '13px', marginBottom: '12px' }}><strong>{t('adminNotes')}</strong> {cd.notes}</div>}
                      {tl.length > 0 && (
                        <div>
                          <p style={{ fontSize: '12px', fontWeight: 600, color: '#888', marginBottom: '8px' }}>{t('adminTimeline')}</p>
                          {tl.map((ev) => (
                            <div key={ev.id} style={{ fontSize: '12px', color: '#555', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                              <strong>{ev.event_type.replace(/_/g, ' ')}</strong>
                              {ev.old_value || ev.new_value ? ` (${ev.old_value || '—'} → ${ev.new_value || '—'})` : ''}
                              <span style={{ color: '#aaa', marginLeft: '8px' }}>{new Date(ev.created_at).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </>
        )}

        {/* ── ANALYTICS TAB ── */}
        {tab === 'analytics' && (
          <>
            <div style={s.headerRow}>
              <h2 style={s.title}>{t('adminTabAnalytics')}</h2>
              <button style={s.btnSecondary} onClick={loadAnalytics}>{t('adminRefresh')}</button>
            </div>

            {analyticsLoading && <p style={s.msg}>{t('adminLoading')}</p>}

            {!analyticsLoading && analytics && <AnalyticsPanel analytics={analytics} t={t} s={s} />}
          </>
        )}

      </div>
    </div>
  );
}

const s = {
  wrap: { minHeight: '100vh', background: '#f4f5f7', fontFamily: 'Inter, sans-serif' },
  nav: {
    background: '#1a1a2e', color: '#fff', padding: '0 32px',
    height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  navLogo: { fontWeight: 700, fontSize: '15px', letterSpacing: '0.05em' },
  navR: { display: 'flex', alignItems: 'center', gap: '16px' },
  navCount: { fontSize: '13px', color: '#aaa' },
  logoutBtn: {
    background: 'transparent', border: '1px solid #444', color: '#ccc',
    padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
  },
  shell: { maxWidth: '1200px', margin: '0 auto', padding: '28px 24px' },
  statsRow: { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' },
  statCard: {
    flex: '1 1 140px', background: '#fff', borderRadius: '12px', padding: '18px 20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '4px',
  },
  statValue: { fontSize: '28px', fontWeight: 800, lineHeight: 1 },
  statLabel: { fontSize: '12px', color: '#888', fontWeight: 500 },
  tabRow: { display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '0' },
  tab: {
    padding: '10px 20px', borderRadius: '8px 8px 0 0', border: 'none',
    background: 'transparent', fontSize: '14px', fontWeight: 500, color: '#666', cursor: 'pointer',
    borderBottom: '2px solid transparent', marginBottom: '-1px',
  },
  tabActive: { color: '#1a1a2e', borderBottom: '2px solid #1a1a2e', fontWeight: 700 },
  badge: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: '#f59e0b', color: '#fff', borderRadius: '10px',
    padding: '1px 7px', fontSize: '11px', fontWeight: 700, marginLeft: '6px',
  },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '20px', fontWeight: 700, color: '#1a1a2e', margin: 0 },
  actions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  btnSecondary: {
    padding: '8px 14px', borderRadius: '8px', border: '1px solid #ddd',
    background: '#fff', fontSize: '13px', cursor: 'pointer', fontWeight: 500,
  },
  btnPrimary: {
    padding: '8px 14px', borderRadius: '8px', border: 'none',
    background: '#5b6af0', color: '#fff', fontSize: '13px', cursor: 'pointer', fontWeight: 600,
  },
  search: {
    width: '100%', padding: '10px 14px', borderRadius: '8px',
    border: '1px solid #ddd', fontSize: '14px', marginBottom: '16px',
    boxSizing: 'border-box', outline: 'none',
  },
  tableWrap: { background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { padding: '12px 16px', background: '#f0f1f5', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e8e8e8' },
  td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', color: '#333', verticalAlign: 'middle' },
  link: { color: '#5b6af0', fontWeight: 500, textDecoration: 'none' },
  msg: { textAlign: 'center', color: '#888', padding: '40px' },
  cardList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  requestCard: {
    background: '#fff', borderRadius: '12px', padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb',
  },
  requestCardTop: { display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' },
  requestUser: { fontSize: '14px', fontWeight: 700, color: '#1a1a2e', marginBottom: '6px' },
  requestDesc: { fontSize: '13px', color: '#555', lineHeight: 1.5, marginBottom: '8px' },
  requestMeta: { fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', gap: '8px' },
  requestActions: { display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'flex-start' },
  statusPill: {
    display: 'inline-block', borderRadius: '20px', padding: '2px 10px',
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
  },
  actionBtn: {
    padding: '5px 12px', borderRadius: '6px', border: '1px solid #e5e7eb',
    background: '#f9fafb', color: '#374151', fontSize: '12px', cursor: 'pointer', fontWeight: 500,
  },
  toast: {
    position: 'fixed', bottom: '24px', right: '24px',
    background: '#1a1a2e', padding: '12px 20px',
    borderRadius: '8px', fontSize: '13px', zIndex: 9999,
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)', border: '1px solid',
  },
};
